import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { apiCallStatus } from 'helpers/constants';
import { GeneralSelect, InfoMessage, PopOutChart } from 'sharedComponents';
import LoadingStatusWrapper from 'sharedComponents/WithLoadingStatus/LoadingStatusWrapper';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';

import useChartConfig, { chartPlugins } from './useChartConfig';
import { opButtons } from '../../constants';
import useReloadTab from '../../useReloadTab';

import { EnableUniqueScaling, PopOutButton, ResetZoomButton, withLeftButtonGroup, withPopOutRow, withRightButtonGroup } from 'sharedComponents/Chart/headers';
import { getTranslatedHistoScatterV2KPIName } from 'helpers/translateHelper';
import useChartResize from 'sharedHooks/useChartResize';

const HistogramComponent = ({
    model: {
        dateSelection,
        histogramData,
        histogramDataConfig,
        histogramDataLoadingStatus,
        histogramKpis,
        histogramKpisLoadingStatus,
        selectedKpi,
        errorData,
    },
    actions: {
        getHistogramForAsset,
        getHistogramKPIsForAsset,
        setSelectedKpi,
        removeSelectedKpiLegend
    },
    activeTab,
    asset,
}) => {
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const [isZoomed, setIsZoomed] = useState(false);
    const containerRef = useRef();
    const chartRef = useRef();

    useChartResize(containerRef, chartRef);
    const refreshData = () => {
        if (histogramKpisLoadingStatus === apiCallStatus.SUCCESS &&
            activeTab === opButtons.histogramV2 &&
            histogramKpis?.length &&
            selectedKpi &&
            dateSelection.from &&
            dateSelection.to) {
            getHistogramForAsset(dateSelection.from, dateSelection.to, asset, selectedKpi);
        }
    };

    useEffect(() => {
        setIsZoomed(false);
    }, [dateSelection]);

    useReloadTab(refreshData,
        opButtons.histogramV2,
        [selectedKpi, histogramKpisLoadingStatus, dateSelection]);

    useEffect(() => {
        histogramKpisLoadingStatus !== apiCallStatus.SUCCESS &&
            getHistogramKPIsForAsset(asset);
    }, []);

    const options = useChartConfig(histogramData, histogramDataConfig, histogramKpis, removeSelectedKpiLegend);

    const HistogramKPISelector = () => {
        return < GeneralSelect
            id='histogram_chart_type_selector'
            placeholder={translate('ABB.Powertrain.Frontend.pleaseSelectKPI')}
            options={histogramKpis}
            value={selectedKpi || ''}
            onChange={setSelectedKpi}
            getOptionLabel={option => getTranslatedHistoScatterV2KPIName(option)}
            getOptionValue={option => option.histogramKey} />;
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([HistogramKPISelector, EnableUniqueScaling]),
        withRightButtonGroup([ResetZoomButton, PopOutButton])]);

    const PopOutChartHeader = withPopOutRow([
        withRightButtonGroup([ResetZoomButton, PopOutButton])
    ]);

    if (errorData?.loadingStatus === apiCallStatus.ERROR) {
        return <ErrorView
            errorCode={errorData.errorCode}
            onRetry={() => getHistogramKPIsForAsset(asset)}
            onReportIssue={showReportIssue}
        />;
    }

    return histogramKpisLoadingStatus === apiCallStatus.SUCCESS && histogramKpis?.length === 0 ?
        <Row className='button-row-container'>
            <Col className='table-empty-content'>
                <InfoMessage title='ABB.Powertrain.Frontend.noSupportedMeasurementTypes' />
            </Col>
        </Row> :
        <LoadingStatusWrapper
            useCustomAnchor
            transparent
            loadingStatus={[histogramKpisLoadingStatus, histogramDataLoadingStatus]}>
            <div ref={containerRef} className='histogram-chart-container'>
                <PopOutChart
                    chartRef={chartRef}
                    isZoomed={isZoomed}
                    setIsZoomed={setIsZoomed}
                    hasKpi={!!selectedKpi}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.chartNoKPIsSelected'
                        text='ABB.Powertrain.Frontend.chartNoKPIsSelectedUserGuide' />}
                    hasData={!!histogramData?.length}
                    renderNoData={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.adjustDateRangeOrChartType' />}
                    options={options}
                    modules={chartPlugins}
                    loadingStatus={histogramDataLoadingStatus}
                    container='op-histogram-chart'
                    mainChartHeader={MainChartHeader}
                    popOutChartHeader={PopOutChartHeader}
                    isNotPreserveSignals={true}
                />
            </div>
        </LoadingStatusWrapper>;
};

export default HistogramComponent;

HistogramComponent.propTypes = {
    actions: PropTypes.shape({
        getHistogramForAsset: PropTypes.func,
        getHistogramKPIsForAsset: PropTypes.func,
        setSelectedKpi: PropTypes.func,
        removeSelectedKpiLegend: PropTypes.func,
    }),
    model: PropTypes.shape({
        activeDetailedInformationTab: PropTypes.string,
        dateSelection: PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
        }),
        selectedKpi: PropTypes.object,
        histogramData: PropTypes.array,
        histogramDataConfig: PropTypes.object,
        histogramDataLoadingStatus: PropTypes.string,
        histogramKpis: PropTypes.array,
        histogramKpisLoadingStatus: PropTypes.string,
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.number,
        }),
    }),
    activeTab: PropTypes.string,
    asset: PropTypes.object,
};
